/**
 * DEMO ANSWERS
 * ---------------------------------------------------------------------------
 * The model call needs an API key. Without one the backend replies with a
 * fallback sentence, which shows none of the answer anatomy - citations,
 * confidence, conflicts, the data a question still needs.
 *
 * When that fallback comes back, the UI substitutes one of these worked
 * examples and tags the turn "Demo answer" so it is never mistaken for a real
 * result. Delete this file once the model call works.
 */

import React from 'react';

export interface DemoStep {
  label: string;
  source: string;
  color: string;
  tag: string;
  time: string;
}

export interface DemoAnswer {
  steps: DemoStep[];
  body: React.ReactNode;
  confidence: number;
  confidenceNote?: string;
  followups: string[];
}

/** The backend's canned reply when the model call fails. */
export function isFallbackAnswer(content: string): boolean {
  if (!content) return false;
  return (
    /having trouble generating a response/i.test(content) ||
    /please try again/i.test(content) && content.length < 400
  );
}

const Cite: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="cite">{children}</span>
);

const Ak: React.FC<{ l: string; v: string; d?: string; dir?: 'up' | 'dn'; color?: string }> = ({
  l,
  v,
  d,
  dir,
  color,
}) => (
  <div className="ak">
    <div className="l">{l}</div>
    <div className="v" style={color ? { color } : undefined}>
      {v}
    </div>
    {d && <div className={`d ${dir === 'up' ? 'up' : dir === 'dn' ? 'dn' : ''}`}>{d}</div>}
  </div>
);

const Need: React.FC<{ title: string; rows: [string, string, string][] }> = ({ title, rows }) => (
  <div className="need">
    <div className="h">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <ellipse cx="12" cy="6" rx="8" ry="3" />
        <path d="M4 6v12c0 1.7 3.6 3 8 3s8-1.3 8-3V6" />
      </svg>
      {title}
    </div>
    {rows.map(([label, tag, tone]) => (
      <div className="r" key={label}>
        <span>{label}</span>
        <span className={`s tg tg-${tone}`}>{tag}</span>
      </div>
    ))}
  </div>
);

/* ------------------------------------------------------------------ margin */

const margin: DemoAnswer = {
  steps: [
    { label: 'Resolved "gross margin" → certified metric GM-01', source: 'semantic layer', color: '#6D5AE6', tag: 'SL', time: '0.1s' },
    { label: 'Pulled Q2 revenue and COGS by product line', source: 'SAP S/4HANA', color: '#0B66C3', tag: 'SAP', time: '1.4s' },
    { label: 'Pulled discount and deal-desk approvals', source: 'Salesforce', color: '#00A1E0', tag: 'SF', time: '0.9s' },
    { label: 'Read the FY26 budget model assumptions', source: 'Excel · Budget_FY26_v7.xlsx', color: '#107C41', tag: 'XL', time: '0.6s' },
    { label: 'Reconciled revenue across the three systems', source: 'reconciler', color: '#D9910E', tag: 'RC', time: '0.3s' },
  ],
  confidence: 84,
  confidenceNote: 'the remaining 12% is unexplained; see below',
  followups: [
    'Which deals drove the discounting?',
    'Model a 15% discount cap for Q3',
    'Which receivables are most at risk?',
  ],
  body: (
    <>
      <p>
        Gross margin fell from <b>41.2% to 37.6%</b> in Q2 <Cite>SAP</Cite> — a 3.6-point drop worth
        roughly <b>$1.9M</b> of gross profit against the same revenue mix. Three things explain 88% of
        it, in order of size:
      </p>
      <div className="ans-kpis">
        <Ak l="GM Q1" v="41.2%" />
        <Ak l="GM Q2" v="37.6%" d="▼ 3.6 pts" dir="dn" color="var(--red)" />
        <Ak l="GP impact" v="−$1.9M" />
        <Ak l="Explained" v="88%" />
      </div>
      <p>
        <b>1. Discounting on enterprise deals — 1.7 pts.</b> Average discount on deals above $250K rose
        from 11% to 19% <Cite>Salesforce</Cite>, and 14 of those deals were approved above the 15%
        deal-desk threshold. Nine of the 14 closed in the final two weeks of the quarter.
      </p>
      <p>
        <b>2. Freight and input costs — 1.2 pts.</b> Inbound freight per unit rose 23% in May{' '}
        <Cite>SAP</Cite>, and the Egypt entity absorbed an FX-driven input cost increase that has not
        yet been passed to pricing.
      </p>
      <p>
        <b>3. Product mix — 0.3 pts.</b> Lower-margin hardware grew faster than software subscriptions.
      </p>
      <div className="warnbox">
        <b>One conflict you should know about.</b> Salesforce reports Q2 revenue $180K higher than SAP.
        The difference is two deals marked Closed Won in Salesforce that SAP has not yet invoiced. I
        used SAP, because the certified definition of revenue is <i>invoiced, net of credit notes</i>{' '}
        <Cite>GM-01</Cite>.
      </div>
      <Need
        title="To explain the last 12% I would need"
        rows={[
          ['Warehouse-level landed cost by SKU', 'in SAP, not mapped', 'a'],
          ['Returns and credit notes posted after 30 June', 'available Monday', 'n'],
          ['Regional freight contracts (Excel, finance share)', 'no access granted', 'r'],
        ]}
      />
    </>
  ),
};

/* -------------------------------------------------------------------- cash */

const cash: DemoAnswer = {
  steps: [
    { label: 'Resolved "cash" → certified metric CASH-02', source: 'semantic layer', color: '#6D5AE6', tag: 'SL', time: '0.1s' },
    { label: 'Pulled bank balances across 6 accounts', source: 'SAP S/4HANA', color: '#0B66C3', tag: 'SAP', time: '0.9s' },
    { label: 'Pulled AR ageing and collection history', source: 'SAP + Odoo', color: '#714B67', tag: 'AR', time: '1.3s' },
    { label: 'Pulled payroll run-rate', source: 'Workday', color: '#F5A623', tag: 'WD', time: '0.5s' },
    { label: 'Read the 13-week cash model', source: 'Excel · Cash_13wk.xlsx', color: '#107C41', tag: 'XL', time: '0.8s' },
  ],
  confidence: 72,
  confidenceNote: 'sensitive to one customer',
  followups: [
    'What if Cairo Fintech pays in November?',
    'Which receivables are most at risk?',
    'Draft a cash note for the board',
  ],
  body: (
    <>
      <p>
        My central estimate is <b>$6.4M at 31 December</b>, in a range of <b>$5.1M – $7.3M</b>. The
        range is wide for one reason: collections.
      </p>
      <div className="ans-kpis">
        <Ak l="Cash today" v="$8.1M" />
        <Ak l="Q4 central" v="$6.4M" />
        <Ak l="Downside" v="$5.1M" color="var(--red)" />
        <Ak l="Runway" v="14 mo" />
      </div>
      <p>
        You hold $8.1M across six accounts today <Cite>SAP</Cite>. Payroll and fixed costs consume
        about $1.9M a month <Cite>Workday</Cite>. The swing factor is{' '}
        <b>$7.6M of receivables over 60 days old</b> <Cite>SAP</Cite> — historically you collect 58% of
        that bucket within a quarter, and the range above is that assumption flexed ±15 points.
      </p>
      <div className="warnbox">
        <b>Your 13-week model disagrees with me by $0.9M.</b> It assumes the Cairo Fintech Group
        balance ($1.2M, 96 days overdue) is collected in November <Cite>Cash_13wk</Cite>. Nothing in
        SAP or email suggests a payment plan has been agreed. I excluded it from the central case.
      </div>
    </>
  ),
};

/* ---------------------------------------------------------------- decision */

const decide: DemoAnswer = {
  steps: [
    { label: 'Classified as a decision, not a lookup', source: 'OmniMind', color: '#D97757', tag: 'OM', time: '0.1s' },
    { label: 'Decomposed into 11 required inputs', source: 'decision model', color: '#D97757', tag: 'OM', time: '0.4s' },
    { label: 'Checked which inputs exist in connected systems', source: 'all sources', color: '#6D5AE6', tag: 'SL', time: '1.1s' },
    { label: 'Pulled KSA pipeline and won revenue', source: 'Salesforce', color: '#00A1E0', tag: 'SF', time: '0.8s' },
    { label: 'Pulled cost-to-serve for GCC accounts', source: 'SAP S/4HANA', color: '#0B66C3', tag: 'SAP', time: '1.2s' },
  ],
  confidence: 55,
  confidenceNote: 'directionally positive, not yet costed',
  followups: ['Open the Decision Room', 'Who should own the missing inputs?', 'Compare with a partner-led model'],
  body: (
    <>
      <p>
        This is a decision, not a lookup, so before answering I checked what it actually depends on. I
        can answer <b>6 of the 11</b> inputs from your systems today. Here is what the data says, and
        what it does not.
      </p>
      <div className="ans-kpis">
        <Ak l="KSA pipeline" v="$14.2M" d="▲ 62% YoY" dir="up" />
        <Ak l="KSA won TTM" v="$4.8M" />
        <Ak l="GCC cost-to-serve" v="18.4%" d="vs 11.2% Egypt" dir="dn" />
        <Ak l="Inputs available" v="6/11" />
      </div>
      <p>
        <b>What the data supports.</b> KSA pipeline has grown 62% year on year <Cite>Salesforce</Cite>{' '}
        and now exceeds the threshold your 2027 plan set for a local presence <Cite>Strategy_2027</Cite>
        . Cost-to-serve for GCC accounts is <b>18.4%</b> against 11.2% in Egypt <Cite>SAP</Cite> —
        mostly travel and delayed onboarding.
      </p>
      <p>
        <b>What it does not yet support.</b> I cannot see a local hiring cost model, a Saudization
        compliance plan, or any analysis of whether the pipeline converts at the same rate once you are
        local. Those three are the difference between a good idea and a costed one.
      </p>
      <Need
        title="Data required for this decision — 5 of 11 missing"
        rows={[
          ['Local salary benchmarks and hiring cost', 'not in any system', 'r'],
          ['Saudization and entity setup requirements', 'needs legal input', 'r'],
          ['Office and lease cost in Riyadh', 'not in any system', 'r'],
          ['Conversion rate with local presence', 'estimable from UAE', 'a'],
          ['Board appetite and capital allocation', 'last board pack, partial', 'a'],
        ]}
      />
      <p>
        I would not recommend deciding on this alone. I have opened a <b>Decision Room</b> with the
        five missing inputs assigned to owners, so the decision can be taken once they are in.
      </p>
    </>
  ),
};

/* ------------------------------------------------------------------ growth */

const pipeline: DemoAnswer = {
  steps: [
    { label: 'Resolved "pipeline" → certified metric PIPE-01', source: 'semantic layer', color: '#6D5AE6', tag: 'SL', time: '0.1s' },
    { label: 'Pulled open opportunities by stage and region', source: 'Salesforce', color: '#00A1E0', tag: 'SF', time: '1.1s' },
    { label: 'Pulled win/loss notes for slipped deals', source: 'Salesforce + email', color: '#6D5AE6', tag: 'EM', time: '0.7s' },
    { label: 'Compared against the same point last quarter', source: 'warehouse', color: '#29B5E8', tag: 'SN', time: '0.4s' },
  ],
  confidence: 79,
  followups: ['Which deals slipped and why?', 'Show coverage by region', 'What would close the gap to plan?'],
  body: (
    <>
      <p>
        Q4 pipeline stands at <b>$42M</b> against a $38M plan <Cite>Salesforce</Cite> — but coverage is
        thinner than the headline suggests: <b>58% of it sits in 11 deals</b>, and four of those have
        slipped at least one quarter already.
      </p>
      <div className="ans-kpis">
        <Ak l="Qualified pipeline" v="$42M" d="▲ 18%" dir="up" />
        <Ak l="Coverage vs plan" v="2.1x" d="target 3.0x" dir="dn" color="var(--amber)" />
        <Ak l="Deals > $250K" v="11" />
        <Ak l="Slipped ≥ 1 qtr" v="4" color="var(--red)" />
      </div>
      <p>
        <b>The risk is concentration, not volume.</b> If the three largest deals slip out of Q4, you
        land at $29M — below plan even though total pipeline grew. Two of the three are waiting on the
        same procurement committee <Cite>win/loss</Cite>.
      </p>
      <Need
        title="To call the quarter properly I would need"
        rows={[
          ['Committed forecast from each regional lead', 'not submitted yet', 'a'],
          ['Procurement calendar for the top 3 accounts', 'not in any system', 'r'],
        ]}
      />
    </>
  ),
};

/* ----------------------------------------------------------------- generic */

const generic: DemoAnswer = {
  steps: [
    { label: 'Classified the question and resolved its terms', source: 'semantic layer', color: '#6D5AE6', tag: 'SL', time: '0.2s' },
    { label: 'Checked which connected sources can answer it', source: 'planner', color: '#D97757', tag: 'OM', time: '0.6s' },
    { label: 'Queried the matching systems in parallel', source: 'agents', color: '#2354F0', tag: 'AG', time: '1.3s' },
  ],
  confidence: 68,
  confidenceNote: 'demo content — no model key configured',
  followups: ['Why did our gross margin drop last quarter?', 'How much cash will we have at the end of Q4?'],
  body: (
    <>
      <p>
        This is a worked example of how an answer arrives: the reasoning trace above shows which
        systems were read, every figure carries the source it came from, and the confidence score
        below reflects coverage, freshness and conflicts rather than the model's tone.
      </p>
      <p>
        Connect a model key and this turn is replaced by a real answer over your own data — the shape
        of it stays exactly as you see here.
      </p>
      <Need
        title="What this answer would need from your systems"
        rows={[
          ['A model API key for the reasoning step', 'not configured', 'r'],
          ['At least one active source for the question', 'ready', 'g'],
        ]}
      />
    </>
  ),
};

/** Pick the worked example that best fits the question asked. */
export function matchDemoAnswer(question: string): DemoAnswer {
  const q = (question || '').toLowerCase();

  if (/margin|profit|gross|cogs|discount/.test(q)) return margin;
  if (/cash|runway|liquid|receivable|collect/.test(q)) return cash;
  if (/should we|open|expand|riyadh|office|invest|hire|acqui|decide/.test(q)) return decide;
  if (/pipeline|deal|forecast|quota|risk|churn|revenue driver/.test(q)) return pipeline;
  return generic;
}
